import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { 
  Wind,
  Info,
  AlertTriangle,
  CheckCircle2,
  Activity,
  Gauge,
  Settings,
  Zap,
  Baby,
  Droplets,
  Snowflake,
  Heart
} from 'lucide-react';

export default function ModesVentilatoiresPage() {
  const [selectedMode, setSelectedMode] = useState(null);

  const modesVentilatoires = [
    {
      id: 'oap',
      name: 'OAP (Œdème Aigu Pulmonaire)',
      icon: Droplets,
      color: 'bg-cyan-500/10 border-cyan-500/30',
      badgeColor: 'bg-cyan-500',
      iconColor: 'text-cyan-400',
      params: {
        fio2: '100%',
        volumeCourant: '10-12 ml/kg',
        fr: '16-18 /min',
        ie: 'Non spécifié',
        pep: '+/- (selon tolérance)',
        pression: 'Non spécifiée'
      },
      remarques: [
        '⚠️ Collapsus de reventilation fréquent',
        'Surveillance hémodynamique rapprochée obligatoire',
        'Prévoir remplissage vasculaire si chute tensionnelle',
        'Diminuer progressivement la FiO2 selon SpO2'
      ],
      clinical: 'L\'OAP nécessite une ventilation avec FiO2 maximale initialement. Le collapsus de reventilation est une complication fréquente due à la levée de l\'hyperpression thoracique après intubation.'
    },
    {
      id: 'asthme',
      name: 'Asthme Aigu Grave',
      icon: Wind,
      color: 'bg-red-500/10 border-red-500/30',
      badgeColor: 'bg-red-500',
      iconColor: 'text-red-400',
      params: {
        fio2: '100% puis ↓ suivant SpO2',
        volumeCourant: '8-10 ml/kg',
        fr: '10-12 /min',
        ie: '1/3 (inspiration courte, expiration longue)',
        pep: '0 cm H2O',
        pression: '<35 cm H2O (Pression de plateau)'
      },
      remarques: [
        '🔴 CRITIQUE : Sonde de large diamètre (8-8,5 mm)',
        'Éviter ABSOLUMENT que le patient lutte contre le respirateur',
        'Sédation profonde souvent nécessaire',
        'Hypercapnie permissive (accepter PaCO2 élevée)',
        'Surveillance du barotraumatisme (Pplat <35)',
        'Aspiration bronchique régulière'
      ],
      clinical: 'L\'asthme aigu grave nécessite une ventilation protectrice avec volumes faibles et fréquence basse pour permettre l\'expiration complète. Le rapport I/E de 1/3 permet une expiration prolongée essentielle.'
    },
    {
      id: 'bpco',
      name: 'Décompensation BPCO',
      icon: Wind,
      color: 'bg-amber-500/10 border-amber-500/30',
      badgeColor: 'bg-amber-500',
      iconColor: 'text-amber-400',
      params: {
        fio2: '100% puis ↓ suivant SpO2',
        volumeCourant: '6-8 ml/kg',
        fr: '10-12 /min',
        ie: '1/3 (inspiration courte, expiration longue)',
        pep: '0 cm H2O',
        pression: 'Non spécifiée'
      },
      remarques: [
        'Sonde de large diamètre (possibilité d\'aspiration)',
        'Hypercapnie permissive (tolérer PaCO2 élevée)',
        'Auto-PEP (PEP intrinsèque) à surveiller',
        'Diminuer FiO2 rapidement si SpO2 > 92%',
        'Éviter hyperventilation (piégeage d\'air)'
      ],
      clinical: 'La BPCO décompensée nécessite une ventilation similaire à l\'asthme avec volumes faibles et expiration prolongée. L\'auto-PEP (air trapping) est un risque majeur.'
    },
    {
      id: 'traumatise',
      name: 'Traumatisé',
      icon: AlertTriangle,
      color: 'bg-red-500/10 border-red-500/30',
      badgeColor: 'bg-red-500',
      iconColor: 'text-red-400',
      params: {
        fio2: '100%',
        volumeCourant: '5-8 ml/kg',
        fr: '16 /min',
        ie: 'Non spécifié',
        pep: 'Selon lésions',
        pression: 'Non spécifiée'
      },
      remarques: [
        'Ventilation protectrice (volumes faibles)',
        'Surveiller pneumothorax (drainage si besoin)',
        'PEP adaptée selon lésions pulmonaires',
        'Éviter hypercapnie si traumatisme crânien',
        'Normocapnie visée si TC (PaCO2 35-40 mmHg)'
      ],
      clinical: 'Chez le traumatisé, la ventilation protectrice avec petits volumes est essentielle. En cas de traumatisme crânien associé, maintenir une normocapnie stricte.'
    },
    {
      id: 'eclampsie',
      name: 'Éclampsie',
      icon: Zap,
      color: 'bg-purple-500/10 border-purple-500/30',
      badgeColor: 'bg-purple-500',
      iconColor: 'text-purple-400',
      params: {
        fio2: '100%',
        volumeCourant: '12 ml/kg',
        fr: '12 /min',
        ie: 'Non spécifié',
        pep: 'Selon tolérance',
        pression: 'Non spécifiée'
      },
      remarques: [
        'Sédation profonde pour contrôle des crises',
        'Surveillance tensionnelle stricte',
        'Prévenir inhalation (estomac plein)',
        'Maintenir normocapnie',
        'Prévoir extraction fœtale rapide'
      ],
      clinical: 'L\'éclampsie nécessite une ventilation avec volumes standards et sédation profonde pour contrôler les crises convulsives. La patiente est considérée à estomac plein.'
    },
    {
      id: 'noyade',
      name: 'Noyade',
      icon: Droplets,
      color: 'bg-blue-500/10 border-blue-500/30',
      badgeColor: 'bg-blue-500',
      iconColor: 'text-blue-400',
      params: {
        fio2: '100%',
        volumeCourant: '6-8 ml/kg',
        fr: 'Selon hématose',
        ie: 'Non spécifié',
        pep: 'Selon lésions',
        pression: '<30 cm H2O'
      },
      remarques: [
        'Ventilation protectrice (ARDS possible)',
        'PEP souvent nécessaire (recrutement alvéolaire)',
        'Surveiller œdème pulmonaire lésionnel',
        'Aspiration bronchique si besoin',
        'Hypothermie associée fréquente'
      ],
      clinical: 'La noyade entraîne souvent un ARDS nécessitant une ventilation protectrice avec PEP pour recruter les alvéoles collabées. L\'hypothermie associée est fréquente.'
    },
    {
      id: 'hypothermie',
      name: 'Hypothermie',
      icon: Snowflake,
      color: 'bg-cyan-500/10 border-cyan-500/30',
      badgeColor: 'bg-cyan-500',
      iconColor: 'text-cyan-400',
      params: {
        fio2: '100%',
        volumeCourant: '6-8 ml/kg',
        fr: '8-10 /min',
        ie: 'Non spécifié',
        pep: 'Selon tolérance',
        pression: 'Non spécifiée'
      },
      remarques: [
        'Fréquence basse (métabolisme ralenti)',
        'Réchauffement progressif obligatoire',
        'Surveillance des troubles du rythme',
        'Éviter mobilisation brutale (FV)',
        'Maintenir sédation pendant réchauffement'
      ],
      clinical: 'L\'hypothermie sévère nécessite une ventilation avec fréquence basse adaptée au métabolisme ralenti. Le réchauffement doit être progressif pour éviter les troubles du rythme.'
    },
    {
      id: 'nouveau-ne',
      name: 'Nouveau-né',
      icon: Baby,
      color: 'bg-pink-500/10 border-pink-500/30',
      badgeColor: 'bg-pink-500',
      iconColor: 'text-pink-400',
      params: {
        fio2: 'Selon SpO2 cible',
        volumeCourant: '10 ml/kg',
        fr: 'NN à terme = 30 /min | Prématuré = 50 /min',
        ie: '1/2',
        pep: '3 cm H2O',
        pression: '10-20 cm H2O'
      },
      remarques: [
        '🍼 Paramètres spécifiques pédiatriques',
        'Fréquence élevée adaptée au nouveau-né',
        'PEP systématique pour maintenir CRF',
        'Pressions faibles (compliance thoracique)',
        'SpO2 cible : 90-95% (éviter hyperoxie)',
        'Prématuré : FR encore plus élevée (50/min)'
      ],
      clinical: 'Le nouveau-né nécessite des paramètres très spécifiques avec fréquences élevées et pressions basses. La PEP de 3 est systématique pour maintenir la capacité résiduelle fonctionnelle.'
    }
  ];

  const selectedModeData = modesVentilatoires.find(m => m.id === selectedMode);

  return (
    <div className="min-h-screen bg-[hsl(var(--bg))] text-[hsl(var(--text))] p-4 md:p-6" data-testid="modes-ventilatoires-page">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="text-center space-y-3">
          <div className="flex items-center justify-center gap-3">
            <Wind className="w-10 h-10 text-cyan-400" />
            <h1 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-cyan-400 to-cyan-300 bg-clip-text text-transparent">
              Modes Ventilatoires sous Respirateur
            </h1>
          </div>
          <p className="text-[hsl(var(--text-dim))] text-sm md:text-base max-w-3xl mx-auto">
            Guide complet des paramètres de ventilation mécanique par pathologie
          </p>
        </div>

        {/* Info générale */}
        <Alert className="bg-cyan-500/10 border-cyan-500/30">
          <Info className="w-4 h-4 text-cyan-400" />
          <AlertDescription className="text-[hsl(var(--text))]">
            <p className="font-semibold mb-2">Rappels Fondamentaux :</p>
            <ul className="text-sm space-y-1 text-[hsl(var(--text-dim))]">
              <li>• <strong>FiO2</strong> : Fraction inspirée en oxygène (21-100%)</li>
              <li>• <strong>Volume courant</strong> : Volume d'air par cycle respiratoire (ml/kg)</li>
              <li>• <strong>FR</strong> : Fréquence respiratoire (cycles/minute)</li>
              <li>• <strong>I/E</strong> : Rapport inspiration/expiration</li>
              <li>• <strong>PEP</strong> : Pression expiratoire positive (cm H2O)</li>
              <li>• <strong>Pression</strong> : Pression de plateau ou pic (cm H2O)</li>
            </ul>
          </AlertDescription>
        </Alert>

        {/* Sélection du mode */}
        <Card className="bg-[hsl(var(--bg-elev))] border-[hsl(var(--border))]">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-cyan-300">
              <Settings className="w-5 h-5" />
              Sélectionnez une Pathologie
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              {modesVentilatoires.map((mode) => {
                const ModeIcon = mode.icon;
                const isSelected = selectedMode === mode.id;
                
                return (
                  <button
                    key={mode.id}
                    onClick={() => setSelectedMode(mode.id)}
                    className={`p-4 rounded-lg border-2 transition-all text-left ${
                      isSelected
                        ? `${mode.color} border-current shadow-lg scale-105`
                        : 'bg-[hsl(var(--bg))] border-[hsl(var(--border))] hover:border-cyan-500/30'
                    }`}
                    data-testid={`mode-${mode.id}`}
                  >
                    <div className="flex items-center gap-3 mb-2">
                      <div className={`w-10 h-10 rounded-full ${mode.color} flex items-center justify-center`}>
                        <ModeIcon className={`w-5 h-5 ${mode.iconColor}`} />
                      </div>
                      <Badge className={`${mode.badgeColor} text-white text-xs`}>
                        {mode.id === 'nouveau-ne' ? 'Pédiatrique' : 'Adulte'}
                      </Badge>
                    </div>
                    <h3 className="font-semibold text-[hsl(var(--text))]">{mode.name}</h3>
                  </button>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Détails du mode sélectionné */}
        {selectedModeData && (
          <>
            {/* Paramètres */}
            <Card className={`${selectedModeData.color} border-2`} data-testid="mode-details">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-[hsl(var(--text))]">
                  <Gauge className={`w-5 h-5 ${selectedModeData.iconColor}`} />
                  Paramètres Ventilatoires - {selectedModeData.name}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {/* FiO2 */}
                  <div className="p-4 rounded-lg bg-[hsl(var(--bg))] border border-[hsl(var(--border))]">
                    <div className="flex items-center gap-2 mb-2">
                      <Wind className="w-4 h-4 text-cyan-400" />
                      <span className="text-xs font-semibold text-[hsl(var(--text-dim))]">FiO2</span>
                    </div>
                    <p className="text-lg font-bold text-[hsl(var(--text))]">{selectedModeData.params.fio2}</p>
                  </div>

                  {/* Volume courant */}
                  <div className="p-4 rounded-lg bg-[hsl(var(--bg))] border border-[hsl(var(--border))]">
                    <div className="flex items-center gap-2 mb-2">
                      <Activity className="w-4 h-4 text-cyan-400" />
                      <span className="text-xs font-semibold text-[hsl(var(--text-dim))]">Volume Courant</span>
                    </div>
                    <p className="text-lg font-bold text-[hsl(var(--text))]">{selectedModeData.params.volumeCourant}</p>
                  </div>

                  {/* FR */}
                  <div className="p-4 rounded-lg bg-[hsl(var(--bg))] border border-[hsl(var(--border))]">
                    <div className="flex items-center gap-2 mb-2">
                      <Heart className="w-4 h-4 text-cyan-400" />
                      <span className="text-xs font-semibold text-[hsl(var(--text-dim))]">Fréquence Respiratoire</span>
                    </div>
                    <p className="text-lg font-bold text-[hsl(var(--text))]">{selectedModeData.params.fr}</p>
                  </div>

                  {/* I/E */}
                  <div className="p-4 rounded-lg bg-[hsl(var(--bg))] border border-[hsl(var(--border))]">
                    <div className="flex items-center gap-2 mb-2">
                      <Gauge className="w-4 h-4 text-cyan-400" />
                      <span className="text-xs font-semibold text-[hsl(var(--text-dim))]">Rapport I/E</span>
                    </div>
                    <p className="text-lg font-bold text-[hsl(var(--text))]">{selectedModeData.params.ie}</p>
                  </div>

                  {/* PEP */}
                  <div className="p-4 rounded-lg bg-[hsl(var(--bg))] border border-[hsl(var(--border))]">
                    <div className="flex items-center gap-2 mb-2">
                      <Zap className="w-4 h-4 text-cyan-400" />
                      <span className="text-xs font-semibold text-[hsl(var(--text-dim))]">PEP</span>
                    </div>
                    <p className="text-lg font-bold text-[hsl(var(--text))]">{selectedModeData.params.pep}</p>
                  </div>

                  {/* Pression */}
                  <div className="p-4 rounded-lg bg-[hsl(var(--bg))] border border-[hsl(var(--border))]">
                    <div className="flex items-center gap-2 mb-2">
                      <Gauge className="w-4 h-4 text-cyan-400" />
                      <span className="text-xs font-semibold text-[hsl(var(--text-dim))]">Pression</span>
                    </div>
                    <p className="text-lg font-bold text-[hsl(var(--text))]">{selectedModeData.params.pression}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Contexte clinique */}
            <Alert className={`${selectedModeData.color} border-none`}>
              <Info className={`w-4 h-4 ${selectedModeData.iconColor}`} />
              <AlertDescription>
                <p className="font-semibold text-[hsl(var(--text))] mb-1">Contexte Clinique :</p>
                <p className="text-sm text-[hsl(var(--text-dim))]">{selectedModeData.clinical}</p>
              </AlertDescription>
            </Alert>

            {/* Remarques importantes */}
            <Card className="bg-[hsl(var(--bg-elev))] border-[hsl(var(--border))]">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-amber-300">
                  <AlertTriangle className="w-5 h-5" />
                  Points Clés & Surveillance
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  {selectedModeData.remarques.map((remarque, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <CheckCircle2 className="w-4 h-4 text-cyan-400 mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-[hsl(var(--text-dim))]">{remarque}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </>
        )}

        {/* Tableau récapitulatif */}
        <Card className="bg-[hsl(var(--bg-elev))] border-[hsl(var(--border))]">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-cyan-300">
              <Activity className="w-5 h-5" />
              Tableau Récapitulatif Complet
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[hsl(var(--border))]">
                    <th className="text-left p-3 font-semibold text-[hsl(var(--text))]">Pathologie</th>
                    <th className="text-left p-3 font-semibold text-[hsl(var(--text))]">FiO2</th>
                    <th className="text-left p-3 font-semibold text-[hsl(var(--text))]">Vt (ml/kg)</th>
                    <th className="text-left p-3 font-semibold text-[hsl(var(--text))]">FR</th>
                    <th className="text-left p-3 font-semibold text-[hsl(var(--text))]">I/E</th>
                    <th className="text-left p-3 font-semibold text-[hsl(var(--text))]">PEP</th>
                    <th className="text-left p-3 font-semibold text-[hsl(var(--text))]">Pression</th>
                  </tr>
                </thead>
                <tbody>
                  {modesVentilatoires.map((mode, idx) => (
                    <tr 
                      key={mode.id}
                      className={`border-b border-[hsl(var(--border))] hover:bg-[hsl(var(--bg))] cursor-pointer transition-colors ${
                        selectedMode === mode.id ? mode.color : ''
                      }`}
                      onClick={() => setSelectedMode(mode.id)}
                    >
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          <Badge className={`${mode.badgeColor} text-white text-xs`}>
                            {idx + 1}
                          </Badge>
                          <span className="font-medium text-[hsl(var(--text))]">{mode.name}</span>
                        </div>
                      </td>
                      <td className="p-3 text-[hsl(var(--text-dim))]">{mode.params.fio2}</td>
                      <td className="p-3 text-[hsl(var(--text-dim))]">{mode.params.volumeCourant}</td>
                      <td className="p-3 text-[hsl(var(--text-dim))]">{mode.params.fr}</td>
                      <td className="p-3 text-[hsl(var(--text-dim))]">{mode.params.ie}</td>
                      <td className="p-3 text-[hsl(var(--text-dim))]">{mode.params.pep}</td>
                      <td className="p-3 text-[hsl(var(--text-dim))]">{mode.params.pression}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
